import React from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { ChillerCircuit, CircuitComponent } from "./CatalogAdmin.types.tsx";
import { normalizeModel } from "./model.tsx";

type ComponentKey = "condensers" | "fans" | "coils";

const COMPONENTS: ComponentKey[] = ["condensers", "fans", "coils"];

interface CircuitCardProps {
  index: number;
  circuit: ChillerCircuit;
  centrifugal: boolean;
  knownCompressors: string[];
  errors: string[];
  canRemove: boolean;
  onChange: (circuit: ChillerCircuit) => void;
  onRemove: () => void;
}

const componentOf = (model: string, quantity: string): CircuitComponent | null =>
  model.trim() === "" && quantity === ""
    ? null
    : {
        model: model.trim() === "" ? null : model,
        quantity: quantity === "" ? 0 : Number(quantity),
      };

const CircuitCard: React.FC<CircuitCardProps> = ({
  index,
  circuit,
  centrifugal,
  knownCompressors,
  errors,
  canRemove,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const number = index + 1;
  const compressors = circuit.compressors ?? [];

  const matchOf = (model: string) =>
    knownCompressors.find((known) => normalizeModel(known) === normalizeModel(model));

  const setCompressor = (position: number, model: string) =>
    onChange({
      ...circuit,
      compressors: compressors.map((current, i) => (i === position ? model : current)),
    });

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Card.Title className="mb-0">{t("catalog.chiller.circuit", { number })}</Card.Title>
          {canRemove && (
            <Button size="sm" variant="link" className="text-danger" onClick={onRemove}>
              {t("catalog.chiller.removeCircuit")}
            </Button>
          )}
        </div>

        {errors.map((message) => (
          <Alert key={message} variant="danger">
            {message}
          </Alert>
        ))}

        <Form.Label>{t("catalog.chiller.compressors")}</Form.Label>
        {compressors.map((model, position) => {
          const label = t("catalog.chiller.compressorLabel", {
            number: position + 1,
            circuit: number,
          });

          return (
            <div key={position} className="d-flex gap-2 mb-2">
              {centrifugal ? (
                <Form.Control
                  aria-label={label}
                  value={model}
                  onChange={(event) => setCompressor(position, event.target.value)}
                />
              ) : (
                <Form.Select
                  aria-label={label}
                  value={matchOf(model) ?? model}
                  onChange={(event) => setCompressor(position, event.target.value)}
                >
                  {!matchOf(model) && (
                    <option value={model}>{`${model} ${t("catalog.chiller.notInCatalog")}`}</option>
                  )}
                  {knownCompressors.map((known) => (
                    <option key={known} value={known}>
                      {known}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Button
                size="sm"
                variant="outline-danger"
                aria-label={t("catalog.chiller.removeCompressor", {
                  number: position + 1,
                  circuit: number,
                })}
                onClick={() =>
                  onChange({
                    ...circuit,
                    compressors: compressors.filter((_, i) => i !== position),
                  })
                }
              >
                ×
              </Button>
            </div>
          );
        })}
        <Button
          size="sm"
          variant="outline-secondary"
          className="mb-3"
          onClick={() =>
            onChange({
              ...circuit,
              compressors: [...compressors, centrifugal ? "" : (knownCompressors[0] ?? "")],
            })
          }
        >
          {t("catalog.chiller.addCompressor")}
        </Button>

        {COMPONENTS.map((key) => {
          const component = t(`catalog.chiller.${key}`);

          return (
            <Row key={key} className="mb-2">
              <Col xs={8}>
                <Form.Control
                  list={`chiller-${key}`}
                  aria-label={t("catalog.chiller.componentOf", { component, circuit: number })}
                  placeholder={component}
                  value={circuit[key]?.model ?? ""}
                  onChange={(event) =>
                    onChange({
                      ...circuit,
                      [key]: componentOf(event.target.value, String(circuit[key]?.quantity ?? "")),
                    })
                  }
                />
              </Col>
              <Col xs={4}>
                <Form.Control
                  type="number"
                  min={0}
                  aria-label={t("catalog.chiller.quantityOf", { component, circuit: number })}
                  placeholder={t("catalog.chiller.quantity")}
                  value={circuit[key]?.quantity ?? ""}
                  onChange={(event) =>
                    onChange({
                      ...circuit,
                      [key]: componentOf(circuit[key]?.model ?? "", event.target.value),
                    })
                  }
                />
              </Col>
            </Row>
          );
        })}

        <Form.Control
          list="chiller-evaporators"
          aria-label={t("catalog.chiller.evaporatorOf", { circuit: number })}
          placeholder={t("catalog.chiller.evaporator")}
          value={circuit.evaporator ?? ""}
          onChange={(event) =>
            onChange({
              ...circuit,
              evaporator: event.target.value === "" ? null : event.target.value,
            })
          }
        />
      </Card.Body>
    </Card>
  );
};

export default CircuitCard;
